import React, { useState, useRef, useEffect, Ref } from 'react';
import { useReactToPrint } from 'react-to-print';
import { httpGet, httpPost } from '../../../modules/Backend';
import { getCurrentFormattedDate } from '../../../helpers/DateHelper';
import LoadingButton from '../LoadingButton';
import { EntityNames } from '../../../enums/EntityNames';

interface PrintableComponentProps {
  data: any;
  locationSummary: any;
  ref: React.RefObject<HTMLDivElement>
}

// Printable component with a ref to allow printing
const PrintableComponent = React.forwardRef<
  HTMLDivElement,
  PrintableComponentProps
>(({ data }, ref) => {
  if (!data) return null;

  return (
    <div ref={ref}>
      <div className="print-pick-list__wrapper">
        <div className="print-pick-list__header">
          <p>{getCurrentFormattedDate()}</p>
        </div>
        <div className="returns-print-pick-list__page">
          {data.packingBoxes.map((packingBox: any, i: number) => (
            <div className="print-pick-list__item-wrapper">
              <div className="returns-print-pick-list__box-info-wrapper">
                <div className="grid-x returns-print-pick-list__box-info">
                  <p className="small-2">Box Ref</p>
                  <p className="small-10">{packingBox.boxReference}</p>
                </div>

                <div className="grid-x returns-print-pick-list__box-info">
                  <p className="small-2">From</p>
                  <p className="small-10">{packingBox.oldBreadcrumb}</p>
                </div>

                <div className="grid-x returns-print-pick-list__box-info">
                  <p className="small-2">To</p>
                  <p className="small-10">{packingBox.breadcrumb}</p>
                </div>

                <div className="grid-x returns-print-pick-list__box-info">
                  <p className="small-2">Sequence number</p>
                  <p className="small-10">{packingBox.boxSequence}</p>
                </div>

                <div className="grid-x returns-print-pick-list__box-info">
                  <p className="small-2">Volume</p>
                  <p className="small-10">{packingBox.boxVolume}</p>
                </div>

                <div className="grid-x returns-print-pick-list__box-info">
                  <p className="small-2">Owned by</p>
                  <p className="small-10">{packingBox.ownedBy}</p>
                </div>

                <div className="grid-x returns-print-pick-list__box-info">
                  <p className="small-2">Charged for</p>
                  <p className="small-10">{packingBox.isChargedFor ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

interface PrinterProps {
  packingBoxIds: string[];
}

function PrintPackingBoxReturnList({ packingBoxIds }: PrinterProps) {
  const [data, setData] = useState<any[]>();
  const [locationSummary, setLocationSummary] = useState<any>({});
  const [shouldPrint, setShouldPrint] = useState(false);
  const [loading, setLoading] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);

  const fetchData = async () => {
    const payLoad = {
      packingBoxIds: packingBoxIds
    };
    const [printDataResponse] = await Promise.all([
      httpPost(`api/${EntityNames.packingBox}/returnsPrintData`, payLoad)
    ]);

    if (printDataResponse.data) {
      setData(printDataResponse.data);
      setShouldPrint(true);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => { setShouldPrint(false); setLoading(false) }
  });

  useEffect(() => {
    if (shouldPrint && componentRef.current) {
      handlePrint();
    }
  }, [shouldPrint]);

  const handleFetchAndPrint = async () => {
    if (loading)
      return
    setLoading(true)
    await fetchData();
  };

  return (
    <div>
      <LoadingButton className="button button--secondary button--with-icon button--large"
        onPress={handleFetchAndPrint}
        isDisabled={loading}
        isLoading={loading}
        loadingText="Preparing"
        buttonText="Print">
        <></>
      </LoadingButton>
      <div style={{ display: 'none' }}>
        <PrintableComponent ref={componentRef} data={data} locationSummary={locationSummary} />
      </div>
    </div>
  );
};

export default PrintPackingBoxReturnList;